import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { convertMinor, todayInTz, LANGUAGES, type CurrencyCode, type Language } from '@trestly/shared';
import { useRate, useSettings, useUpdateSettings } from '../api/hooks';
import { CurrencyToggle } from '../components/CurrencyToggle';
import { ErrorState } from '../components/EmptyState';
import { InfoNote } from '../components/InfoNote';
import { ScreenHeader } from '../components/ScreenHeader';
import { Spinner } from '../components/Spinner';
import { Toggle } from '../components/Toggle';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { errorText } from '../lib/errors';
import { formatIsoDate } from '../lib/format';
import { moneyLabel } from '../lib/money';
import { confirmAction, haptic, useBackButton } from '@/telegram';
import { LANGUAGE_NAMES, t } from '../i18n';

/** Дни 1..31 для выбора начала периода. */
const CYCLE_DAYS = Array.from({ length: 31 }, (_, index) => index + 1);

export function Settings(): JSX.Element {
  const settings = useSettings();
  const updateSettings = useUpdateSettings();
  const today = todayInTz();
  const rate = useRate(today);
  const navigate = useNavigate();

  const goHome = useCallback(() => navigate('/'), [navigate]);
  // Системная «назад» в клиенте: из меню Telegram история может быть пустой,
  // поэтому ведём не «на шаг назад», а на главную.
  useBackButton(goHome);

  /**
   * Смена дня начала периода пересобирает историю: траты 1–24 числа при начале
   * с 25-го уезжают в предыдущий период. Поэтому сначала спрашиваем, а при
   * отказе возвращаем контрол к текущему значению — React его сам не откатит.
   */
  const changeCycleStartDay = async (select: HTMLSelectElement): Promise<void> => {
    const current = settings.data?.cycleStartDay ?? 1;
    const cycleStartDay = Number(select.value);
    if (cycleStartDay === current) return;

    const windowText =
      cycleStartDay === 1
        ? t('settingsPage.confirmCalendar')
        : t('settingsPage.confirmWindow', { day: cycleStartDay });
    const ok = await confirmAction(
      t('settingsPage.confirmText', { window: windowText }),
      { title: t('settingsPage.confirmTitle'), okText: t('settingsPage.confirmOk') },
    );
    if (!ok) {
      select.value = String(current);
      return;
    }
    haptic.select();
    updateSettings.mutate({ cycleStartDay });
  };

  return (
    <>
      <ScreenHeader title={t('settingsPage.title')} onBack={goHome} period={false} />
      <div className="px-4">

      {settings.isPending ? (
        <Spinner block />
      ) : settings.isError ? (
        <ErrorState message={errorText(settings.error)} onRetry={() => void settings.refetch()} />
      ) : (
        <>
          <Card className="mb-3 p-4">
            <div className="flex items-center justify-between gap-3">
              <span className="text-[15px] font-medium">{t('settings.language')}</span>
              <select
                className="h-9 shrink-0 rounded-lg border border-border bg-card px-2 text-[15px] disabled:opacity-60"
                value={settings.data.language}
                disabled={updateSettings.isPending}
                aria-label={t('settings.language')}
                onChange={(event) => {
                  haptic.select();
                  updateSettings.mutate({ language: event.currentTarget.value as Language });
                }}
              >
                {LANGUAGES.map((lang) => (
                  <option key={lang} value={lang}>
                    {LANGUAGE_NAMES[lang]}
                  </option>
                ))}
              </select>
            </div>
          </Card>

          <Card className="mb-3 p-4">
            <div className="mb-3 flex items-baseline justify-between gap-2">
              <span className="text-[15px] font-medium">{t('settingsPage.baseCurrency')}</span>
              <span className="text-[13px] text-muted-foreground">
                {settings.data.baseCurrency}
              </span>
            </div>
            <CurrencyToggle
              value={settings.data.baseCurrency}
              disabled={updateSettings.isPending}
              onChange={(baseCurrency: CurrencyCode) => {
                haptic.result('success');
                updateSettings.mutate({ baseCurrency });
              }}
            />
            <p className="mt-3 text-[13px] leading-snug text-muted-foreground">
              {t('settingsPage.baseCurrencyHint')}
            </p>
          </Card>

          <Card className="mb-3 p-4">
            <div className="flex items-center justify-between gap-3">
              <span className="text-[15px] font-medium">{t('settingsPage.rollover')}</span>
              <Toggle
                checked={settings.data.rolloverEnabled}
                label={t('settingsPage.rollover')}
                disabled={updateSettings.isPending}
                onChange={(rolloverEnabled) => updateSettings.mutate({ rolloverEnabled })}
              />
            </div>
            <p className="mt-2 text-[13px] leading-snug text-muted-foreground">
              {t('settingsPage.rolloverHint')}
            </p>
          </Card>

          <Card className="mb-3 p-4">
            <div className="flex items-center justify-between gap-3">
              <span className="text-[15px] font-medium">{t('settingsPage.cycleStart')}</span>
              <select
                className="h-9 shrink-0 rounded-lg border border-border bg-card px-2 text-[15px] tnum disabled:opacity-60"
                value={settings.data.cycleStartDay}
                disabled={updateSettings.isPending}
                aria-label={t('settingsPage.cycleStartAria')}
                onChange={(event) => void changeCycleStartDay(event.currentTarget)}
              >
                {CYCLE_DAYS.map((day) => (
                  <option key={day} value={day}>
                    {day === 1 ? t('settingsPage.cycleDayMonth') : t('settingsPage.cycleDay', { day })}
                  </option>
                ))}
              </select>
            </div>
            <p className="mt-2 text-[13px] leading-snug text-muted-foreground">
              {t('settingsPage.cycleHint')}
            </p>
            {settings.data.cycleStartDay === 1 ? null : (
              <p className="mt-2 text-[13px] leading-snug text-muted-foreground">
                {t('settingsPage.cycleShortMonth')}
              </p>
            )}
          </Card>

          {updateSettings.isError ? (
            <ErrorState message={errorText(updateSettings.error)} />
          ) : null}
        </>
      )}

      <Card className="mb-3 p-4">
        <div className="mb-2 flex items-baseline justify-between gap-2">
          <span className="text-[15px] font-medium">{t('settingsPage.rate')}</span>
          {rate.data?.stale ? <Badge variant="warn">{t('settingsPage.rateStale')}</Badge> : null}
        </div>
        {rate.isPending ? (
          <Spinner small />
        ) : rate.isError ? (
          <div className="text-[13px] text-muted-foreground">
            {t('settingsPage.rateUnavailable', { error: errorText(rate.error) })}
          </div>
        ) : (
          <>
            {/* 1 единица валюты в минорных (100) → BYN, арифметика только из shared */}
            <div className="num text-[26px] font-medium">
              1 {rate.data.quote} = {moneyLabel(convertMinor(100, rate.data.ratePpm), 'BYN')}
            </div>
            <div className="mt-1 text-[13px] text-muted-foreground">
              {t('settingsPage.rateOn', { date: formatIsoDate(rate.data.onDate) })}
            </div>
            {rate.data.stale ? (
              <p className="mt-2 text-[13px] text-muted-foreground">
                {t('settingsPage.rateStaleHint')}
              </p>
            ) : null}
          </>
        )}
      </Card>

      <InfoNote>
        {t('settingsPage.rateNote')}
      </InfoNote>
      </div>
    </>
  );
}
